/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { formatDateWithFormat } from '../../../../helpers/date'
import { TableColumn } from '../../../../components/common/Table/TableHeader'
import { TableRow } from '../../../../components/common/Table/TableRows'
import { Table } from '../../../../components/common/Table'
import { getPendingInvoices } from '../../../../api/admin'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { largeNumberFormattingWithSpaces } from '../../../../helpers/number'
import { Button, ButtonVariant } from '../../../../components/common/Button'
import { Dropdown } from '../../../../components/common/Dropdown'
import { IconChoices } from '../../../../components/common/Icon'
import { useModal } from '../../../../hooks/useModal'
import { EditSocietyInvoiceModal } from '../Societies/EditSocietyInvoiceModal'
import { DateTimeInput } from '../../../../components/common/DateTimeInput'
import { Label } from '../../../../components/common/Label/Label'

export const PendingInvoices = (): JSX.Element | null => {
  const { translate } = useAppTranslation()

  const [date, setDate] = useState<Date>()
  const { data: pendingInvoicesData, isLoading: loading } = useQuery({
    queryKey: [
      { scope: 'superAdmin', entity: 'pendingInvoices', date },
    ] as const,
    queryFn: async ({ queryKey }) => {
      const response = await getPendingInvoices(
        queryKey[0].date?.toDateString()
      )
      return response.data.data
    },
  })

  const pendingInvoices = useMemo(() => {
    return pendingInvoicesData || []
  }, [pendingInvoicesData])

  const [currentSocietyId, setCurrentSocietyId] = useState<string>()
  const {
    show: showEditSocietyInvoiceModal,
    open: openEditSocietyInvoiceModal,
    close: closeEditSocietyInvoiceModal,
  } = useModal()

  const columns: TableColumn[] = [
    {
      key: 'actions',
      title: 'Åtgärder',
      accessor: '_id',
      renderItem: (_id: string) => {
        return (
          <Dropdown
            dropdownClassName="w-[300px]"
            className="w-fit"
            dropdownPosition="bottom-right"
            options={[
              {
                value: 'editSocietyInvoice',
                label: 'Redigera fakturering',
                icon: IconChoices.EDIT,
                onClick: () => {
                  setCurrentSocietyId(_id)
                  openEditSocietyInvoiceModal()
                },
              },
            ]}
          >
            <Button
              label="Åtgärder"
              iconPlacement="left"
              variant={ButtonVariant.PRIMARY}
            />
          </Dropdown>
        )
      },
    },
    { key: '_id', title: 'ID' },
    { key: 'name', title: 'Name' },
    { key: 'organisationNumber', title: 'Organisationsnummer' },
    { key: 'masterAdminUserId', title: 'Master Admin ID' },
    { key: 'societyTypeDisplayName', title: 'Föreningstyp' },
    { key: 'addressBillingStreet', title: 'Adressrad 2' },
    { key: 'addressBillingCo', title: 'Adressrad 1' },
    { key: 'addressBillingZip', title: 'Postnummer' },
    { key: 'addressBillingCity', title: 'Postort' },
    { key: 'billingEmail', title: 'Fakturamejl' },
    { key: 'discountCode', title: 'rabattkod' },
    { key: 'cost', title: 'Belopp' },
  ]

  const rows: TableRow[] = pendingInvoices.map((invoice) => ({
    key: invoice.society._id,
    data: {
      ...invoice,
      ...invoice.society,
      cost: `${invoice.cost} kr`,
      discountCode: invoice.society.discount?.code,
      invoiceDate: invoice.invoiceDate
        ? formatDateWithFormat(new Date(invoice.invoiceDate), 'yyyy-MM-dd')
        : undefined,
    },
  }))

  const invoiceSumTotal = rows?.reduce((prev, curr) => {
    // data.cost format: 1234 kr
    const costCurrencyString: string = curr.data.cost
    const costString = costCurrencyString.split(' ')[0]
    const cost: number = +costString

    return prev + cost
  }, 0)

  return (
    <div className="h-full w-full space-y-4">
      <div className="flex flex-col gap-2">
        <Label name="date" label="Välj fakturadatum" />
        <div className="flex gap-2">
          <DateTimeInput value={date} onChange={(e) => setDate(e)} />
          {date && <Button onClick={() => setDate(undefined)} label="Rensa" />}
        </div>
      </div>
      {rows?.length > 0 && (
        <>
          <p>
            Antal fakturor: {rows.length}. Totalt belopp:{' '}
            {largeNumberFormattingWithSpaces(invoiceSumTotal)} kr
          </p>

          <Table columns={columns} rows={rows} />
        </>
      )}
      {!loading && pendingInvoices.length === 0 && (
        <p>{translate('adminView.searchResult.emptyResult')}</p>
      )}
      {showEditSocietyInvoiceModal && (
        <EditSocietyInvoiceModal
          show={showEditSocietyInvoiceModal}
          close={closeEditSocietyInvoiceModal}
          societyId={currentSocietyId}
        />
      )}
    </div>
  )
}
